/**
 * AllSitesDashboardPage (CLOUD, `/`) — the whole estate at a glance, as the SAME board of cards
 * a site's dashboard is, with every site's figures added together.
 *
 * It answers the per-site dashboard's questions for all sites at once — how many alerts are
 * open, who on the watch list was seen, how many people are on site, is any machine saturated —
 * instead of making an operator open each site in turn. The board is the shared
 * `DashboardBoardPage`; what makes it All Sites is only what it is fed, the same way the All
 * Sites alert board is the per-site board fed a cross-site api:
 *
 *   - the reads fan out across the sites that are online (`api/multiSiteDashboard`), and the
 *     answers are folded into the one shape the cards read — counts add, series add per bucket,
 *     lists merge newest first;
 *   - the live alert feed is the All Sites alert board's own adapter, so an alert lands here
 *     the moment it lands there;
 *   - every id is SITE-QUALIFIED (`nodeId::id`), which is what routes a row's record, picture
 *     or link back down the tunnel of the site it came from;
 *   - the camera list's "Site" column is the site's name — each site's group tree is hung under
 *     a root named after it.
 *
 * Links: a card that opens a page only a site has (a report, the watch list) has nowhere to go
 * from here, so it simply has no arrow; a record opened from a row offers "Full page" in its own
 * site (`?site=` switches the scope on the way). Managing the sites themselves lives at /sites,
 * reached from the site switcher.
 *
 * Layout: the operator's ONE layout, shared with every site's dashboard (useAccountDashboardLayout).
 */
import { useMemo, useRef } from 'react';
import {
  ApiProvider,
  DashboardBoardPage,
  reportModelSlug,
  useApi,
  type DashboardBoardHost,
  type DashboardMachineRead,
} from '@trinetra/shared';
import { AlertDetailView } from './AlertDetailPage';
import { EventDetailBody } from './AlertInvestigationPage';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import { useAccountDashboardLayout } from '../hooks/useAccountDashboardLayout';
import { SCOPE_PARAM } from '../constants/scope';
import {
  getNodeCameras,
  getNodeGroups,
  getNodeModelAssignments,
  getNodeAiSummary,
  getNodeModelEvents,
  getNodeModelEvent,
  getNodeModelStats,
  getNodeModelEventImage,
} from '../services/api.service';
import { createMultiSiteHexAlertsApi, siteCameraKey } from '../api/multiSiteHexAlerts.adapter';
import {
  createMultiSiteAttendanceApi,
  createMultiSiteModelApi,
  createMultiSiteWatchlistApi,
  fanOut,
  foldStats,
  foldSummaries,
  mergeNewest,
  qualifyEvent,
  rawEvent,
  splitSiteKey,
  toArray,
  type SiteRef,
} from '../api/multiSiteDashboard';

/** The group id each site's tree is hung under, so the camera list's Site column names it. */
const siteRootId = (nodeId: string) => siteCameraKey(nodeId, '__site__');

/** `path` with the scope that opens it in `nodeId`'s site. */
function inSite(path: string, nodeId: string) {
  return `${path}${path.includes('?') ? '&' : '?'}${SCOPE_PARAM}=${encodeURIComponent(nodeId)}`;
}

/** Where the board's site-only links go from here — see the header. */
function resolvePath(path: string, about?: string | null): string | null {
  const ref = about ? splitSiteKey(about) : null;
  if (ref?.nodeId) {
    // The record's own site, with its bare id wherever the link spelled the qualified one.
    const bare = path
      .split(encodeURIComponent(about!)).join(encodeURIComponent(ref.cameraId))
      .split(about!).join(ref.cameraId);
    return inSite(bare, ref.nodeId);
  }
  if (path === '/alert-board') return '/all-sites/alert-board';
  if (path === '/sites') return path;
  return null;
}

/**
 * The node registry's load, for the estate: memory and disk are totals, CPU is the busiest
 * site, uptime the most recent restart — the same rules the local node uses for its machines.
 */
function estateMetrics(nodes: Array<{ metrics?: any }>) {
  const ms = nodes.map((n) => n.metrics).filter((m) => m && typeof m === 'object');
  if (ms.length === 0) return {};
  const sum = (k: string) => ms.reduce((acc, m) => acc + (Number(m[k]) || 0), 0);
  const memUsed = sum('memoryUsedMB');
  const memTotal = sum('memoryTotalMB');
  const uptimes = ms.map((m) => Number(m.uptimeSeconds) || 0).filter((u) => u > 0);
  return {
    cpuPercent: Math.max(...ms.map((m) => Number(m.cpuPercent) || 0)),
    memoryPercent: memTotal > 0
      ? (memUsed / memTotal) * 100
      : ms.reduce((acc, m) => acc + (Number(m.memoryPercent) || 0), 0) / ms.length,
    memoryUsedMB: memUsed,
    memoryTotalMB: memTotal,
    storageUsedGB: sum('storageUsedGB'),
    storageTotalGB: sum('storageTotalGB'),
    uptimeSeconds: uptimes.length ? Math.min(...uptimes) : undefined,
    timestamp: Date.now(),
  };
}

export default function AllSitesDashboardPage() {
  const { nodes } = useNode();
  const { hasPermission } = useAuth();
  const rootApi = useApi();
  const { layoutKey, layoutRemote } = useAccountDashboardLayout();

  // Which sites the live alert feed covers, as a value that only changes with the roster —
  // the feed rebuilds every socket when its api changes, and the node list re-polls every 10 s.
  // Offline sites are included, as on the All Sites alert board: their socket retries and
  // their alerts arrive the moment the tunnel is back.
  const rosterKey = useMemo(() => nodes.map((n) => n.nodeId).sort().join(','), [nodes]);
  const roster = useMemo<SiteRef[]>(
    () => nodes.map((n) => ({ nodeId: n.nodeId, name: n.name })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rosterKey],
  );

  // Every other read asks only the sites online NOW, read at call time through this ref so a
  // site coming or going changes the next poll without rebuilding the board.
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const onlineSites = (): SiteRef[] =>
    nodesRef.current.filter((n) => n.status === 'online').map((n) => ({ nodeId: n.nodeId, name: n.name }));

  const alertsApi = useMemo(() => createMultiSiteHexAlertsApi(roster), [roster]);
  const api = useMemo(
    () => ({
      ...rootApi,
      hexAlerts: alertsApi,
      watchlist: createMultiSiteWatchlistApi(onlineSites),
      attendance: createMultiSiteAttendanceApi(onlineSites),
      models: createMultiSiteModelApi(onlineSites),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rootApi, alertsApi],
  );

  // Record bodies are component TYPES — built once, or every registry poll would remount an
  // open record. Each reads its site from the qualified id it was handed.
  const records = useMemo(() => {
    const EventRecord: DashboardBoardHost['EventRecord'] = ({ event, model, cameraNameMap, personName, headerActions, onClose }) => {
      const nodeId = splitSiteKey(String(event?.id ?? event?.event_id ?? event?.camera_id ?? '')).nodeId || '';
      // The record is the site's own page body: it gets the site's bare ids and camera names.
      const prefix = siteCameraKey(nodeId, '');
      const siteNames = Object.fromEntries(
        Object.entries(cameraNameMap)
          .filter(([id]) => id.startsWith(prefix))
          .map(([id, name]) => [id.slice(prefix.length), name]),
      );
      return (
        <EventDetailBody
          event={rawEvent(event)}
          model={model}
          nodeId={nodeId}
          cameraNameMap={siteNames}
          personName={(e: any) => personName || e?.matched_person_name || null}
          headerActions={headerActions}
          onClose={onClose}
        />
      );
    };
    const AlertRecord: DashboardBoardHost['AlertRecord'] = ({ alertId, headerActions, onClose }) => (
      <AlertDetailView
        alertId={alertId}
        showTitle={false}
        nodeId={alertsApi.siteOfAlertId(alertId) || undefined}
        headerActions={headerActions}
        onClose={onClose}
      />
    );
    return { EventRecord, AlertRecord };
  }, [alertsApi]);

  const can = {
    cameras: hasPermission('cameras.view'),
    groups: hasPermission('groups.view'),
    models: hasPermission('ai-models.view'),
    assignModels: hasPermission('ai-models.assign'),
    alerts: hasPermission('alerts.view'),
    monitor: hasPermission('monitoring.view'),
    aiEvents: hasPermission('ai_events.view'),
    attendance: hasPermission('attendance.view'),
  };
  const online = nodes.filter((n) => n.status === 'online');

  /** A model read on every site, its rows qualified and merged newest first. */
  const eventsEverywhere = async (model: string, params: Record<string, any>) => {
    // A per-camera read (a counting camera's latest frame) is one site's question.
    const cam = params.camera_id ? splitSiteKey(String(params.camera_id)) : null;
    const sites = cam?.nodeId ? onlineSites().filter((s) => s.nodeId === cam.nodeId) : onlineSites();
    const ask = cam?.nodeId ? { ...params, camera_id: cam.cameraId } : params;
    const res = await fanOut(sites, (s) => getNodeModelEvents(s.nodeId, model, ask));
    const lists = res.ok.map(({ site, value }) => toArray(value).map((r) => qualifyEvent(site.nodeId, r)));
    return mergeNewest(lists, params.limit);
  };

  const host: DashboardBoardHost = {
    layoutKey,
    layoutRemote,
    scopeKey: `all:${rosterKey}`,
    title: 'All Sites',
    subtitle: `Every site's figures together · ${online.length} of ${nodes.length} site${nodes.length === 1 ? '' : 's'} online`,
    subjectNoun: 'estate',
    can,
    monitorPath: hasPermission('nodes.view') ? '/sites' : undefined,
    resolvePath,

    loadMachine: async (): Promise<DashboardMachineRead> => {
      const sites = onlineSites();
      const per = await Promise.all(sites.map(async (site) => {
        const [cams, groups, assignments] = await Promise.allSettled([
          can.cameras ? getNodeCameras(site.nodeId) : Promise.resolve([]),
          can.groups ? getNodeGroups(site.nodeId) : Promise.resolve([]),
          can.models ? getNodeModelAssignments(site.nodeId) : Promise.resolve({}),
        ]);
        return { site, cams, groups, assignments };
      }));

      const cameras: any[] = [];
      const groups: any[] = [];
      const assignments: Record<string, any> = {};
      const failed: string[] = [];
      per.forEach(({ site, cams, groups: grp, assignments: asg }) => {
        const root = siteRootId(site.nodeId);
        groups.push({ id: root, name: site.name, parent_id: null });
        if (grp.status === 'fulfilled') {
          toArray(grp.value).forEach((g: any) => groups.push({
            ...g,
            id: siteCameraKey(site.nodeId, String(g.id)),
            parent_id: g.parent_id ? siteCameraKey(site.nodeId, String(g.parent_id)) : root,
          }));
        }
        if (cams.status === 'fulfilled') {
          toArray(cams.value).forEach((c: any) => {
            const own = (c.groups || []).map((g: any) => ({ ...g, id: siteCameraKey(site.nodeId, String(g.id)) }));
            cameras.push({
              ...c,
              id: siteCameraKey(site.nodeId, String(c.id)),
              // No group, or groups this role can't read: the site itself is the place.
              groups: own.length && grp.status === 'fulfilled' ? own : [{ id: root, name: site.name }],
            });
          });
        }
        if (asg.status === 'fulfilled' && asg.value && typeof asg.value === 'object' && !Array.isArray(asg.value)) {
          Object.entries(asg.value).forEach(([camId, v]) => { assignments[siteCameraKey(site.nodeId, camId)] = v; });
        }
        const lost = [
          cams.status === 'rejected' && 'cameras',
          grp.status === 'rejected' && 'groups',
          asg.status === 'rejected' && 'AI models',
        ].filter(Boolean);
        if (lost.length) failed.push(`${site.name}: ${lost.join(', ')}`);
      });

      const offline = nodesRef.current.length - sites.length;
      return {
        cameras,
        groups,
        assignments,
        metrics: can.monitor ? estateMetrics(nodesRef.current.filter((n) => n.status === 'online')) : {},
        note: `${sites.length} site${sites.length === 1 ? '' : 's'} online${offline ? `, ${offline} offline` : ''} · memory and disk are totals, CPU is the busiest site`,
        failed,
      };
    },
    getSummary: async (surfaces, opts) =>
      foldSummaries(await fanOut(onlineSites(), (s) => getNodeAiSummary(s.nodeId, surfaces, opts))),
    getModelEvents: eventsEverywhere,
    getModelEvent: async (model, id) => {
      const { nodeId, cameraId: bare } = splitSiteKey(String(id));
      if (!nodeId) return null;
      const ev = await getNodeModelEvent(nodeId, model, bare);
      return ev ? qualifyEvent(nodeId, ev) : ev;
    },
    getModelStats: async (model, params) =>
      foldStats(await fanOut(onlineSites(), (s) => getNodeModelStats(s.nodeId, model, params))),
    // Streamed through the row's own site's tunnel — see SiteDashboardPage for why only real
    // image bytes become a picture.
    picture: ({ model, eventId, key }) => ({
      loadImage: async () => {
        const { nodeId, cameraId: bare } = splitSiteKey(eventId);
        if (!nodeId) return null;
        try {
          const blob: any = await getNodeModelEventImage(nodeId, reportModelSlug(model), bare, key);
          if (!blob || (blob.type && !blob.type.startsWith('image/'))) return null;
          return URL.createObjectURL(blob);
        } catch {
          return null;
        }
      },
    }),
    EventRecord: records.EventRecord,
    AlertRecord: records.AlertRecord,
  };

  return (
    <ApiProvider api={api}>
      {/* Keyed on the roster: a site added or removed starts the board over rather than
          leaving the previous estate's figures behind. */}
      <DashboardBoardPage key={rosterKey} host={host} />
    </ApiProvider>
  );
}
